import { nectarApi } from '@/features/nectar/api/client'
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult
} from '@tanstack/react-query'

export type StripeAccount = {
  stripeAccountId: string
  country: string
  payoutsEnabled: boolean
  transfersCapability: string
  requirementsCurrentlyDue: string[]
  onboardingStatus: string
}

export type StripeAccountResponse = {
  data: StripeAccount | null
}

export type StripeOnboardingLinkResponse = {
  data: {
    url: string
    expiresAt: number
  }
}

export const STRIPE_ACCOUNT_QUERY_KEY = ['stripe-account'] as const
export const STRIPE_ONBOARDING_COMPLETE_STATE = 'stripeOnboardingComplete'

export const isStripeAccountPayable = (account: StripeAccount | null | undefined): boolean =>
  account?.payoutsEnabled === true && account.transfersCapability === 'active'

export const stripeAccountNeedsAction = (account: StripeAccount | null | undefined): boolean =>
  (account?.requirementsCurrentlyDue?.length ?? 0) > 0 || account?.onboardingStatus === 'requires_onboarding'

export const useFetchStripeAccount = (enabled = true): UseQueryResult<StripeAccount | null> =>
  useQuery({
    queryKey: STRIPE_ACCOUNT_QUERY_KEY,
    queryFn: async () => {
      const response = await nectarApi.get<StripeAccountResponse>('/v1/user/stripe/account')
      return response.data
    },
    enabled
  })

export const useCreateStripeAccount = (): UseMutationResult<StripeAccount, unknown, void> => {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const response = await nectarApi.post<StripeAccountResponse>('/v1/user/stripe/account')
      if (!response.data) {
        throw new Error('Stripe account was not returned')
      }
      return response.data
    },
    onSuccess: (account) => {
      queryClient.setQueryData(STRIPE_ACCOUNT_QUERY_KEY, account)
    }
  })
}

export const useCreateStripeOnboardingLink = (): UseMutationResult<
  StripeOnboardingLinkResponse['data'],
  unknown,
  void
> =>
  useMutation({
    mutationFn: async () => {
      const response = await nectarApi.post<StripeOnboardingLinkResponse>('/v1/user/stripe/onboarding-link')
      return response.data
    }
  })
